import { useState, type FormEvent } from "react";
import { CONTENT } from "@/content";
import { BorderBeam } from "@/components/ui/border-beam";
import { apiFetch, ApiError, api } from "@/lib/api";
import { funnelState, type ContactData } from "@/lib/funnelState";
import { StepBar } from "./StepBar";
import { Field } from "./Field";

type Props = {
  data: ContactData;
  setData: (d: ContactData) => void;
  onNext: () => void;
  onHome: () => void;
};

type CreateLeadResponse = { id: number; token: string };

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function P2({ data, setData, onNext, onHome }: Props) {
  const p = CONTENT.p2;
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const nameOk = (data.name ?? "").trim().length >= 2;
  const emailOk = emailRe.test(data.email ?? "");
  const phoneOk = (data.phone ?? "").replace(/\D/g, "").length >= 10;
  const valid = nameOk && emailOk && phoneOk;

  const errors = {
    name: touched.name && !nameOk ? "Informe seu nome." : "",
    email: touched.email && !emailOk ? "Informe um e-mail válido." : "",
    phone: touched.phone && !phoneOk ? "Informe um WhatsApp válido." : "",
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setTouched({ name: true, email: true, phone: true });
    setServerError(null);
    if (!valid || submitting) return;

    setSubmitting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const res = await apiFetch<CreateLeadResponse>("/leads", {
        method: "POST",
        body: {
          name: data.name?.trim(),
          email: data.email?.trim().toLowerCase(),
          whatsapp: data.phone?.trim(),
          utm_source: params.get("utm_source") ?? undefined,
          utm_medium: params.get("utm_medium") ?? undefined,
          utm_campaign: params.get("utm_campaign") ?? undefined,
          honeypot: "",
        },
      });
      funnelState.setLeadId(res.id);
      sessionStorage.setItem(api.leadTokenKey, res.token);
      onNext();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setServerError("Muitas tentativas. Tente novamente em alguns minutos.");
      } else {
        setServerError("Não conseguimos enviar agora. Tente novamente em instantes.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fx page-enter">
      <StepBar step={0} onHome={onHome} />
      <main className="fx-main">
        <form className="fx-col fx-col-narrow" onSubmit={submit} noValidate>
          <span className="fx-kicker">Avaliação Diagnóstica</span>
          <h1 className="fx-h">{p.title}</h1>
          <p className="fx-open">{p.open}</p>
          <p className="fx-explain">{p.explain}</p>

          <div className="fx-form">
            <Field
              label={p.fields[0]}
              value={data.name ?? ""}
              onChange={(v) => setData({ ...data, name: v })}
              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              error={errors.name}
            />
            <Field
              label={p.fields[1]}
              type="email"
              value={data.email ?? ""}
              onChange={(v) => setData({ ...data, email: v })}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              error={errors.email}
            />
            <Field
              label={p.fields[2]}
              type="tel"
              value={data.phone ?? ""}
              onChange={(v) => setData({ ...data, phone: v })}
              onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
              error={errors.phone}
            />
            <span className="fx-obs">* {p.obs}</span>
          </div>

          <div className="fx-note">
            <p>{p.reinforce}</p>
          </div>

          {serverError && (
            <div className="adm-login-error" style={{ marginBottom: 16 }}>{serverError}</div>
          )}

          <div className="fx-actions">
            <button
              type="submit"
              className={`btn btn-primary btn-lg btn-beam btn-beam-primary ${valid && !submitting ? "" : "is-wait"}`}
              disabled={submitting}
            >
              <BorderBeam />
              {submitting ? "Enviando…" : p.button} <span className="arr">→</span>
            </button>
          </div>
          <p className="fx-after">{p.after}</p>
        </form>
      </main>
    </div>
  );
}
